import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import LoadingOverlay from '@/components/ui/LoadingOverlay';
import { apiClient, isApiError } from '@/lib/api';
import type {
  LastRunTelemetryResponse,
  LastRunTelemetrySummary,
  LastRunTelemetryEvent,
  GptConcurrencyAnalysis,
  DistributionStatusResponse,
  DistributionReportResponse,
} from '@/types/api';
import { formatDuration, DISTRIBUTION_PIPELINE_STEPS } from '@/types/api';
import {
  buildPipelineSteps,
  getPipelineStatusDisplay,
} from './telemetryHelpers';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

function getStatusBadge(status?: string) {
  const normalized = (status || '').toLowerCase();
  switch (normalized) {
    case 'completed':
      return <Badge variant="success">Completed</Badge>;
    case 'processing':
    case 'running':
      return <Badge variant="primary">Processing</Badge>;
    case 'pending':
      return <Badge variant="warning">Pending</Badge>;
    case 'failed':
      return <Badge variant="danger">Failed</Badge>;
    default:
      return <Badge variant="slate">{status || 'Unknown'}</Badge>;
  }
}

function parseDate(value: unknown): Date | null {
  if (!value) return null;
  if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
}

function computeRunDurationMs(
  summary?: LastRunTelemetrySummary,
  events?: LastRunTelemetryEvent[]
): number | null {
  // Prefer summary timings if available
  const stageKeys = [
    'pdf_ingestion',
    'image_processing',
    'chapter_detection',
    'synthesize_tts',
    'assemble_audio',
    'generate_credits',
    'qa_finalize',
  ];

  let start: Date | null = null;
  let end: Date | null = null;

  if (summary) {
    for (const key of stageKeys) {
      const stage = summary[key] as { started?: string | null; completed?: string | null } | undefined;
      if (stage?.started && !start) {
        const d = parseDate(stage.started);
        if (d) start = d;
      }
      if (stage?.completed) {
        const d = parseDate(stage.completed);
        if (d && (!end || d > end)) end = d;
      }
    }
  }

  // Fallback to event timestamps if needed
  if ((!start || !end) && events && events.length > 0) {
    const times = events
      .map((e) => parseDate(e.timestamp))
      .filter((d): d is Date => !!d)
      .sort((a, b) => a.getTime() - b.getTime());
    if (times.length > 0) {
      start = start || times[0];
      end = end || times[times.length - 1];
    }
  }

  if (!start || !end) return null;
  const diff = end.getTime() - start.getTime();
  return diff > 0 ? diff : null;
}


function isQaCompleted(
  summary: LastRunTelemetrySummary | undefined,
  events: LastRunTelemetryEvent[] | undefined
): boolean {
  try {
    const qa = summary && (summary.qa_finalize as {
      status?: unknown;
      completed?: unknown;
      completed_at?: unknown;
    });

    if (qa) {
      const rawStatus = (qa.status ?? '').toString().toLowerCase();
      if (['completed', 'done', 'success', 'finished', 'complete'].includes(rawStatus)) {
        return true;
      }
      if (!rawStatus && (qa.completed || qa.completed_at)) {
        return true;
      }
    }

    if (Array.isArray(events)) {
      const hit = events.some((e) => {
        const name = (e.event_name || '').toLowerCase();
        return (
          name.includes('qa_finalize_end') ||
          name.includes('qa_end') ||
          (name.includes('qa') && name.includes('_end'))
        );
      });
      if (hit) return true;
    }
  } catch {
    // Ignore and fall through
  }
  return false;
}
interface EventTypeInfo {
  label: string;
  badgeClass: string;
  dotClass: string;
}

function getEventTypeInfo(eventName: string): EventTypeInfo {
  const lower = (eventName || '').toLowerCase();

  if (lower.includes('error')) {
    return {
      label: 'Error',
      badgeClass: 'bg-danger-100 text-danger-700',
      dotClass: 'bg-danger-500',
    };
  }

  if (lower.includes('start')) {
    return {
      label: 'Start',
      badgeClass: 'bg-primary-100 text-primary-700',
      dotClass: 'bg-primary-500',
    };
  }

  if (lower.includes('end')) {
    return {
      label: 'End',
      badgeClass: 'bg-emerald-100 text-emerald-700',
      dotClass: 'bg-emerald-500',
    };
  }

  if (lower.includes('api')) {
    return {
      label: 'API',
      badgeClass: 'bg-amber-100 text-amber-700',
      dotClass: 'bg-amber-500',
    };
  }

  return {
    label: 'Event',
    badgeClass: 'bg-slate-100 text-slate-600',
    dotClass: 'bg-slate-400',
  };
}

function parseEstTimestamp(raw: unknown): Date | null {
  if (!raw) return null;
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;
  let ts = String(raw);
  try {
    if (ts.includes(' EDT')) ts = ts.replace(' EDT', ' GMT-0400');
    if (ts.includes(' EST')) ts = ts.replace(' EST', ' GMT-0500');
    const d = new Date(ts);
    if (!Number.isNaN(d.getTime())) return d;
    const fallback = new Date(String(raw));
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  } catch {
    const d = new Date(String(raw));
    return Number.isNaN(d.getTime()) ? null : d;
  }
}

function processStartsToAnalysis(
  starts: GptConcurrencyAnalysis['starts'],
  timeSliceSeconds: number
): GptConcurrencyAnalysis {
  const sliceMs = Math.max(1, timeSliceSeconds) * 1000;
  const times = (starts ?? [])
    .map((s) => {
      if (typeof s === 'string') return parseEstTimestamp(s);
      if (s && typeof s === 'object') {
        const anyS = s as { timestamp_est?: string; timestamp?: string };
        return parseEstTimestamp(anyS.timestamp_est ?? anyS.timestamp);
      }
      return null;
    })
    .filter((d): d is Date => !!d && !Number.isNaN(d.getTime()))
    .sort((a, b) => a.getTime() - b.getTime());

  if (times.length === 0) {
    return {
      starts,
      time_labels: [],
      concurrency_data: [],
      total_api_calls: 0,
      max_concurrent_calls: 0,
      avg_concurrency: 0,
      efficiency_score: 0,
    };
  }

  const minTime = times[0].getTime();
  const maxTime = times[times.length - 1].getTime();
  const bucketCount = Math.max(1, Math.floor((maxTime - minTime) / sliceMs) + 1);
  const counts = new Array(bucketCount).fill(0);
  const labels = new Array(bucketCount)
    .fill(0)
    .map((_, i) => new Date(minTime + i * sliceMs).toLocaleTimeString());

  times.forEach((t) => {
    const idx = Math.min(bucketCount - 1, Math.floor((t.getTime() - minTime) / sliceMs));
    counts[idx] += 1;
  });

  const totalCalls = times.length;
  const maxConcurrent = counts.reduce((m, v) => Math.max(m, v), 0);
  const avgConcurrent = counts.length ? counts.reduce((a, b) => a + b, 0) / counts.length : 0;
  const efficiency =
    maxConcurrent > 0 ? Math.min(100, (avgConcurrent / maxConcurrent) * 100) : 0;

  return {
    starts,
    time_labels: labels,
    concurrency_data: counts,
    total_api_calls: totalCalls,
    max_concurrent_calls: maxConcurrent,
    avg_concurrency: avgConcurrent,
    efficiency_score: efficiency,
    min_time: new Date(minTime),
    max_time: new Date(maxTime),
    slice_seconds: Math.max(1, timeSliceSeconds),
  };
}

function normalizeConcurrencyData(
  data: GptConcurrencyAnalysis,
  timeSliceSeconds: number
): GptConcurrencyAnalysis {
  if (
    data &&
    Array.isArray(data.starts) &&
    (!Array.isArray(data.time_labels) || !Array.isArray(data.concurrency_data))
  ) {
    return processStartsToAnalysis(data.starts, timeSliceSeconds);
  }
  return data;
}

export default function TTSTelemetryRunPage() {
  const { runId } = useParams<{ runId: string }>();

  const [run, setRun] = useState<LastRunTelemetryResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Distribution pipeline tracking (post-QA)
  const [distributionStatus, setDistributionStatus] = useState<
    Record<string, 'not_started' | 'completed'>
  >({});
  const [distributionIsbn, setDistributionIsbn] = useState<string | null>(null);
  const [distributionTimings, setDistributionTimings] = useState<
    Record<string, { started?: string | null; completed?: string | null }>
  >({});
  const [, setDistributionLoading] = useState(false);
  const [, setDistributionError] = useState<string | null>(null);
  const [qaCompletedForRun, setQaCompletedForRun] = useState(false);

  const [timeSlice, setTimeSlice] = useState(15);
  const [concurrency, setConcurrency] = useState<GptConcurrencyAnalysis | null>(null);
  const [concurrencyLoading, setConcurrencyLoading] = useState(false);
  const [concurrencyError, setConcurrencyError] = useState<string | null>(null);
  const [exportingCsv, setExportingCsv] = useState(false);

  const fetchRun = useCallback(async () => {
    if (!runId) return;
    try {
      setLoading(true);
      setError(null);
      const data = await apiClient.get<LastRunTelemetryResponse>(
        `tts/telemetry/timeline/${runId}`
      );
      setRun(data);
    } catch (err) {
      console.error('Failed to fetch telemetry run:', err);
      setError(err instanceof Error ? err.message : 'Failed to load run telemetry');
      setRun(null);
    } finally {
      setLoading(false);
    }
  }, [runId]);

  const fetchConcurrency = useCallback(
    async (sliceSeconds: number, showLoading = true) => {
      if (!runId) return;
      try {
        if (showLoading) setConcurrencyLoading(true);
        setConcurrencyError(null);
        const raw = await apiClient.get<GptConcurrencyAnalysis>(
          `tts/telemetry/gpt-concurrency/${runId}?time_slice=${sliceSeconds}`
        );
        const normalized = normalizeConcurrencyData(raw, sliceSeconds);
        setConcurrency(normalized);
      } catch (err) {
        console.error('Failed to fetch GPT concurrency:', err);
        setConcurrencyError(
          err instanceof Error ? err.message : 'Failed to load GPT concurrency'
        );
        setConcurrency(null);
      } finally {
        setConcurrencyLoading(false);
      }
    },
    [runId]
  );

  useEffect(() => {
    fetchRun();
  }, [fetchRun]);

  // Track QA completion and resolve ISBN for this run
  useEffect(() => {
    if (!run || !run.run_id) {
      setQaCompletedForRun(false);
      setDistributionIsbn(null);
      setDistributionStatus({});
      setDistributionTimings({});
      setDistributionError(null);
      return;
    }

    const completed = isQaCompleted(
      run.summary as LastRunTelemetrySummary | undefined,
      run.events
    );
    setQaCompletedForRun(completed);

    if (!completed) {
      setDistributionIsbn(null);
      setDistributionStatus({});
      setDistributionTimings({});
      setDistributionError(null);
      return;
    }

    if (distributionIsbn) return;

    const jobId = run.run_id;

    (async () => {
      try {
        // Step 1 – Get job metadata from TTS worker (proxied through backend)
        const meta = await apiClient.get<{ book_name?: string; isbn?: string }>(
          `tts/qa/${jobId}/metadata`
        );

        if (meta && typeof meta.isbn === 'string' && meta.isbn) {
          setDistributionIsbn(meta.isbn);
          setDistributionError(null);
          return;
        }

        const title = meta?.book_name;
        if (!title) {
          setDistributionError('Book title not available in job metadata');
          return;
        }

        // Step 2 – Resolve ISBN via distribution worker using job_id (proxied)
        const res = await apiClient.get<{ isbn?: string }>(
          `distribution/books/getISBN?job_id=${encodeURIComponent(jobId)}`
        );

        if (res && typeof res.isbn === 'string' && res.isbn) {
          setDistributionIsbn(res.isbn);
          setDistributionError(null);
        } else {
          setDistributionError('ISBN not available yet for this title');
        }
      } catch (err) {
        console.error('Failed to resolve ISBN for telemetry run:', err);
        setDistributionError(
          err instanceof Error ? err.message : 'Failed to resolve ISBN for distribution'
        );
      }
    })();
  }, [run, distributionIsbn]);

  // Poll distribution status once ISBN is known and QA is completed
  useEffect(() => {
    if (!qaCompletedForRun || !distributionIsbn) {
      setDistributionStatus({});
      return;
    }

    let cancelled = false;

    let haveReport = false;

    const fetchStatusOnce = async () => {
      try {
        if (cancelled) return;
        const data = await apiClient.get<DistributionStatusResponse>(
          `distribution/status?isbn=${encodeURIComponent(distributionIsbn)}`
        );
        if (cancelled) return;

        const next: Record<string, 'not_started' | 'completed'> = {};

        DISTRIBUTION_PIPELINE_STEPS.forEach((name) => {
          const raw = (data && data[name]) || '';
          const done = raw.toString().toUpperCase() === 'DONE';
          next[name] = done ? 'completed' : 'not_started';
        });

        setDistributionStatus(next);
        setDistributionError(null);
      } catch (err) {
        if (cancelled) return;
        // Treat 404 as "not ready yet" and keep polling without surfacing an error,
        // mirroring the legacy frontend behavior.
        if (isApiError(err) && err.status === 404) {
          return;
        }
        console.error('Failed to fetch distribution status:', err);
        setDistributionError(
          err instanceof Error ? err.message : 'Failed to fetch distribution status'
        );
      } finally {
        if (!cancelled) {
          setDistributionLoading(false);
        }
      }

      // After status, attempt to fetch detailed report if we don't have it yet.
      if (!cancelled && !haveReport) {
        try {
          const report = await apiClient.get<DistributionReportResponse>(
            `distribution/report?isbn=${encodeURIComponent(distributionIsbn)}`
          );

          const nextTimings: Record<string, { started?: string | null; completed?: string | null }> =
            {};

          if (report) {
            /**
             * Helper to robustly extract start/end timestamps for a given distribution step.
             * The distribution worker may expose timings in a few different shapes:
             * - Root-level fields like `${base}_start` / `${base}_end`
             * - Root-level fields like `${base}_started` / `${base}_completed`
             * - Nested objects keyed by the human label (e.g. "Staging copied") with
             *   `started`, `started_at`, `completed`, or `completed_at` properties.
             */
            const extractTimings = (
              baseKey: string,
              labelKey: string
            ): { started?: string | null; completed?: string | null } => {
              const anyReport = report as unknown as Record<string, any>;

              // 1) Try root-level *_start / *_end variants
              const rootStarted =
                anyReport[`${baseKey}_start`] ??
                anyReport[`${baseKey}_started`] ??
                anyReport[`${baseKey}_start_at`] ??
                null;
              const rootCompleted =
                anyReport[`${baseKey}_end`] ??
                anyReport[`${baseKey}_completed`] ??
                anyReport[`${baseKey}_end_at`] ??
                null;

              if (rootStarted || rootCompleted) {
                return {
                  started: rootStarted ?? null,
                  completed: rootCompleted ?? null,
                };
              }

              // 2) Try a nested object keyed by the label or baseKey
              const nested =
                (anyReport[labelKey] as
                  | {
                      started?: string | null;
                      started_at?: string | null;
                      completed?: string | null;
                      completed_at?: string | null;
                    }
                  | undefined) ||
                (anyReport[baseKey] as
                  | {
                      started?: string | null;
                      started_at?: string | null;
                      completed?: string | null;
                      completed_at?: string | null;
                    }
                  | undefined);

              if (nested) {
                return {
                  started: nested.started ?? nested.started_at ?? null,
                  completed: nested.completed ?? nested.completed_at ?? null,
                };
              }

              // 3) Fallback – no timings available
              return { started: null, completed: null };
            };

            nextTimings.unzipping = extractTimings('unzipping', 'unzipping');
            nextTimings['Staging copied'] = extractTimings('staging_copy', 'Staging copied');
            nextTimings['Onix generation'] = extractTimings(
              'onix_generation',
              'Onix generation'
            );
            nextTimings['Publish onix'] = extractTimings('publish_onix', 'Publish onix');
            nextTimings['validate onix'] = extractTimings(
              'validate_onix',
              'validate onix'
            );
          }

          setDistributionTimings(nextTimings);
          haveReport = true;
        } catch (err) {
          // Ignore 404 - report may not be ready yet
          if (isApiError(err) && err.status === 404) {
            return;
          }
          console.error('Failed to fetch distribution report:', err);
        }
      }
    };

    setDistributionLoading(true);
    fetchStatusOnce();

    const interval = setInterval(() => {
      if (!cancelled) {
        fetchStatusOnce();
      }
    }, 3000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [qaCompletedForRun, distributionIsbn]);

  useEffect(() => {
    // Auto-load concurrency once run is available
    if (runId) {
      fetchConcurrency(timeSlice, true);
    }
  }, [runId, timeSlice, fetchConcurrency]);

  const coreSteps = useMemo(
    () =>
      buildPipelineSteps(
        run?.summary as LastRunTelemetrySummary | undefined,
        run?.events,
        run?.status
      ),
    [run]
  );

  const steps = useMemo(() => {
    const base = [...coreSteps];
    if (qaCompletedForRun && DISTRIBUTION_PIPELINE_STEPS.length > 0) {
      DISTRIBUTION_PIPELINE_STEPS.forEach((name) => {
        const status = distributionStatus[name] || 'not_started';
        base.push({
          key: `dist_${name}`,
          label: name,
          status,
        });
      });
    }
    return base;
  }, [coreSteps, qaCompletedForRun, distributionStatus]);

  const statusDisplay = useMemo(() => getPipelineStatusDisplay(coreSteps), [coreSteps]);

  const durationMs = useMemo(
    () => computeRunDurationMs(run?.summary as LastRunTelemetrySummary | undefined, run?.events),
    [run]
  );

  const chartData = useMemo(() => {
    if (!concurrency?.time_labels || !concurrency.concurrency_data) return [];
    return concurrency.time_labels.map((label, idx) => ({
      time: label,
      concurrency: concurrency.concurrency_data?.[idx] ?? 0,
    }));
  }, [concurrency]);

  const events = useMemo(
    () =>
      (run?.events ?? [])
        .slice()
        .sort(
          (a, b) =>
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
        ),
    [run]
  );

  const handleExportCsv = useCallback(() => {
    const effectiveRunId = run?.run_id || runId;
    if (!effectiveRunId || events.length === 0) {
      // eslint-disable-next-line no-alert
      alert('No events available to export for this run.');
      return;
    }

    try {
      setExportingCsv(true);

      const header = ['timestamp', 'event_name', 'type', 'metadata'];

      const rows = events.map((event) => {
        const typeInfo = getEventTypeInfo(event.event_name);
        const metadata =
          event.metadata && Object.keys(event.metadata).length > 0
            ? JSON.stringify(event.metadata)
            : '';
        return [event.timestamp, event.event_name, typeInfo.label, metadata];
      });

      const escapeCell = (value: string) =>
        `"${value.replace(/"/g, '""')}"`;

      const csvLines = [
        header.join(','),
        ...rows.map((row) =>
          row.map((cell) => escapeCell(String(cell ?? ''))).join(',')
        ),
      ];

      const blob = new Blob([csvLines.join('\n')], {
        type: 'text/csv;charset=utf-8;',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `telemetry_${effectiveRunId}_events.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert('Failed to export events CSV. Please try again.');
      console.error('Failed to export telemetry events CSV:', err);
    } finally {
      setExportingCsv(false);
    }
  }, [events, run?.run_id, runId]);

  /**
   * Resolve the start/end timestamps to display for a given pipeline step.
   *
   * - For most steps we read `summary[stepKey].started` / `.completed`.
   * - For "Extraction (Images PNG)" we instead read
   *   `image_processor_png_start` / `image_processor_png_end`.
   * - For "Chapter Detection" we read
   *   `chapter_detector_start` / `chapter_detector_end`.
   *
   * When the source fields are missing we intentionally show `—`.
   */
  const getStepTimestamps = useCallback(
    (step: { key: string; label: string }): { started?: string | null; completed?: string | null } => {
      const summary = run?.summary as LastRunTelemetrySummary | undefined;
      if (!summary) return {};

      const anySummary = summary as Record<string, unknown>;

      // Distribution pipeline steps use timings from the distribution report
      if (step.key.startsWith('dist_')) {
        const timing = distributionTimings[step.label];
        if (timing) {
          return {
            started: timing.started ?? null,
            completed: timing.completed ?? null,
          };
        }
      }

      // Map UI step keys to their corresponding summary keys
      const summaryKeyMap: Record<string, string> = {
        // "Extraction (Images PNG)" should use image_processing timings
        extraction_images: 'image_processing',
        // "Chapter Detection" should use chapter_detection timings
        chapter_detector: 'chapter_detection',
      };

      const summaryKey = summaryKeyMap[step.key] ?? step.key;

      const stage = anySummary[summaryKey] as
        | {
            started?: string | null;
            completed?: string | null;
            started_at?: string | null;
            completed_at?: string | null;
          }
        | undefined;

      return {
        started: stage?.started ?? stage?.started_at ?? null,
        completed: stage?.completed ?? stage?.completed_at ?? null,
      };
    },
    [run?.summary, distributionTimings]
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <span className="text-sm text-slate-500">Loading run telemetry...</span>
      </div>
    );
  }

  if (error || !run) {
    return (
      <div className="space-y-6 animate-fade-in">
        <Link
          to="/tts/telemetry"
          className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          Back to Telemetry
        </Link>

        <Card>
          <CardBody className="text-center py-12">
            <p className="text-lg font-semibold text-slate-800 mb-2">
              Failed to load run telemetry
            </p>
            <p className="text-slate-500 mb-4">{error || 'Run not found'}</p>
            <Button
              variant="outline"
              leftIcon={<ArrowPathIcon className="w-4 h-4" />}
              onClick={fetchRun}
            >
              Retry
            </Button>
          </CardBody>
        </Card>
      </div>
    );
  }

  const durationLabel = durationMs ? formatDuration(durationMs) : '—';

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Back Button */}
      <Link
        to="/tts/telemetry"
        className="inline-flex items-center gap-2 text-slate-500 hover:text-slate-700 transition-colors"
      >
        <ArrowLeftIcon className="w-4 h-4" />
        Back to Telemetry
      </Link>

      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl font-bold text-slate-800">
              Run {run.run_id || runId}
            </h1>
            {getStatusBadge(run.status)}
          </div>
          <p className="text-slate-500">
            Duration: {durationLabel}
          </p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <Card>
          <CardBody className="text-center">
            <p className="text-2xl font-bold text-slate-800">
              {run.total_events ?? '—'}
            </p>
            <p className="text-sm text-slate-500">Total Events</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-2xl font-bold text-slate-800">{durationLabel}</p>
            <p className="text-sm text-slate-500">Duration</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-2xl font-bold text-slate-800">
              {run.summary?.event_counts?.elevenlabsapi_call ?? 0}
            </p>
            <p className="text-sm text-slate-500">ElevenLabs Calls</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-2xl font-bold text-slate-800">
              {run.summary?.event_counts?.gpt_call_start ?? 0}
            </p>
            <p className="text-sm text-slate-500">GPT Calls</p>
          </CardBody>
        </Card>
        <Card>
          <CardBody className="text-center">
            <p className="text-2xl font-bold text-slate-800">
              {run.summary?.event_counts?.grok_call_start ?? 0}
            </p>
            <p className="text-sm text-slate-500">Grok Calls</p>
          </CardBody>
        </Card>
      </div>

      {/* Pipeline Summary */}
      <Card>
        <CardHeader title="Pipeline Steps Summary" />
        <CardBody>
          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-6">
            <div className="flex-1">
              <p className="text-sm text-slate-500 mb-2">Current Status</p>
              <p className="text-lg font-semibold text-slate-800">{statusDisplay}</p>
              <div className="mt-4 space-y-3">
                {steps.map((step) => {
                  const { started, completed } = getStepTimestamps(step);
                  return (
                    <div
                      key={step.key}
                      className="flex items-center justify-between rounded-xl border border-slate-100 px-4 py-3 bg-slate-50"
                    >
                      <div>
                        <p className="text-sm font-medium text-slate-800">
                          {step.label}
                        </p>
                        <p className="text-xs text-slate-500 capitalize">
                          {step.status.replace('_', ' ')}
                        </p>
                      </div>
                      <div className="text-right text-xs text-slate-500">
                        <div>Start: {started || '—'}</div>
                        <div>End: {completed || '—'}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </CardBody>
      </Card>

      {/* GPT/GROK Concurrency Analysis */}
      <LoadingOverlay isLoading={concurrencyLoading} loadingOpacity={40} showSpinner={false}>
        <Card>
          <CardHeader
            title="GPT/GROK Concurrency Analysis"
            subtitle="Concurrent GPT/GROK API calls over time for this run"
            action={
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Time slice (sec)</span>
                  <input
                    type="number"
                    min={1}
                    max={300}
                    value={timeSlice}
                    onChange={(e) => {
                      const val = parseInt(e.target.value || '0', 10);
                      setTimeSlice(Number.isNaN(val) ? 15 : Math.max(1, Math.min(300, val)));
                    }}
                    className="w-20 rounded-lg border border-slate-200 px-2 py-1 text-xs"
                  />
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ArrowPathIcon className={`w-4 h-4 ${concurrencyLoading ? 'animate-spin' : ''}`} />}
                  onClick={() => fetchConcurrency(timeSlice, true)}
                >
                  Refresh
                </Button>
              </div>
            }
          />
          <CardBody>
            {concurrencyError && !concurrency && !concurrencyLoading && (
              <div className="text-center py-6">
                <p className="text-sm text-red-500 mb-2">
                  {concurrencyError}
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  leftIcon={<ArrowPathIcon className="w-4 h-4" />}
                  onClick={() => fetchConcurrency(timeSlice, true)}
                >
                  Try Again
                </Button>
              </div>
            )}

            {!concurrencyError && !concurrency && !concurrencyLoading && (
              <p className="text-sm text-slate-500 text-center py-4">
                No concurrency data available for this run.
              </p>
            )}

            {concurrency && (
              <div className="space-y-6">
                {/* Overview metrics */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <Card>
                    <CardBody className="text-center">
                      <p className="text-2xl font-bold text-slate-800">
                        {concurrency.max_concurrent_calls ?? 0}
                      </p>
                      <p className="text-sm text-slate-500">Max Concurrent Calls</p>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody className="text-center">
                      <p className="text-2xl font-bold text-slate-800">
                        {concurrency.total_api_calls ??
                          (Array.isArray(concurrency.starts)
                            ? concurrency.starts.length
                            : 0)}
                      </p>
                      <p className="text-sm text-slate-500">Total API Calls</p>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody className="text-center">
                      <p className="text-2xl font-bold text-slate-800">
                        {(concurrency.avg_concurrency ?? 0).toFixed(2)}
                      </p>
                      <p className="text-sm text-slate-500">Average Concurrency</p>
                    </CardBody>
                  </Card>
                  <Card>
                    <CardBody className="text-center">
                      <p className="text-2xl font-bold text-slate-800">
                        {`${(concurrency.efficiency_score ?? 0).toFixed(1)}%`}
                      </p>
                      <p className="text-sm text-slate-500">Efficiency Score</p>
                    </CardBody>
                  </Card>
                </div>

                {/* Concurrency Timeline Chart */}
                <div className="h-72">
                  {chartData.length === 0 ? (
                    <p className="text-sm text-slate-500 text-center py-8">
                      No concurrency timeline data available.
                    </p>
                  ) : (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                        <XAxis dataKey="time" stroke="#94A3B8" fontSize={12} />
                        <YAxis stroke="#94A3B8" fontSize={12} allowDecimals={false} />
                        <Tooltip
                          contentStyle={{
                            backgroundColor: 'white',
                            border: '1px solid #E2E8F0',
                            borderRadius: '12px',
                          }}
                        />
                        <Line
                          type="monotone"
                          dataKey="concurrency"
                          stroke="#2563EB"
                          strokeWidth={2}
                          dot={{ fill: '#2563EB' }}
                          name="Concurrent API Calls"
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  )}
                </div>
              </div>
            )}
          </CardBody>
        </Card>
      </LoadingOverlay>

      {/* Events Timeline */}
      <Card>
        <CardHeader
          title="Events Timeline"
          subtitle="All telemetry events for this run"
          action={
            <Button
              variant="outline"
              size="sm"
              leftIcon={<ArrowDownTrayIcon className="w-4 h-4" />}
              onClick={handleExportCsv}
              isLoading={exportingCsv}
            >
              Export CSV
            </Button>
          }
        />
        <CardBody>
          {events.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-4">
              No events available for this run.
            </p>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {events.map((event, index) => {
                const typeInfo = getEventTypeInfo(event.event_name);

                return (
                  <div
                    key={`${index}-${event.timestamp}-${event.event_name}`}
                    className="flex items-start gap-3 py-2 border-b border-slate-50 last:border-b-0"
                  >
                    <span className="font-mono text-xs text-slate-400 w-40 flex-shrink-0">
                      {event.timestamp}
                    </span>
                    <span
                      className={`mt-1.5 h-2 w-2 rounded-full ${typeInfo.dotClass}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3">
                        <p className="text-sm font-medium text-slate-700 break-words">
                          {event.event_name.replace(/_/g, ' ')}
                        </p>
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${typeInfo.badgeClass}`}
                        >
                          {typeInfo.label}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

